// src/components/Personnel.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from './api';
import '../styles/Table.css'; 

const Personnel = () => {
    const [personnel, setPersonnel] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    
    // State for the global password edit switch and draft passwords
    const [isEditingPasswords, setIsEditingPasswords] = useState(false);
    const [passwordDrafts, setPasswordDrafts] = useState({});

    // State for filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [showNoPasswordOnly, setShowNoPasswordOnly] = useState(false);
    const [hideRetailers, setHideRetailers] = useState(true); // Default clicked (true)

    // Searchable Group Dropdown States
    const [groupSearchQuery, setGroupSearchQuery] = useState('');
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const groupDropdownRef = useRef(null);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);

    // Ref for hidden file input
    const fileInputRef = useRef(null);

    const fetchPersonnel = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/personnel');
            setPersonnel(response.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch personnel data.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPersonnel();
    }, [fetchPersonnel]);

    // Close searchable dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
                setIsGroupDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset to page 1 when any filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedGroup, selectedRole, showNoPasswordOnly, hideRetailers, itemsPerPage]);

    const handleSync = async () => {
        try {
            setIsSyncing(true);
            const response = await api.post('/personnel/sync');
            const { inserted, updated, deleted } = response.data.stats;
            alert(`Sync complete!\n\nInserted: ${inserted}\nUpdated: ${updated}\nDeleted: ${deleted}`);
            await fetchPersonnel();
        } catch (err) {
            console.error("Sync failed", err);
            alert("Failed to sync with external API.");
        } finally {
            setIsSyncing(false);
        }
    };

    // Handle CSV Upload
    const handleCsvUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setIsUploading(true);
            const response = await api.post('/personnel/upload-passwords', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const { stats, errorCsv } = response.data;
            
            if (errorCsv) {
                alert(`Upload complete with some errors!\n\nPasswords Updated: ${stats.updated}\nFailed/Skipped: ${stats.skipped}\n\nAn error log CSV will now download automatically.`);
                
                // Trigger automatic download of the error CSV
                const blob = new Blob([errorCsv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.setAttribute("href", url);
                link.setAttribute("download", `password_upload_errors_${new Date().toISOString().slice(0,10)}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert(`Upload complete!\n\nPasswords Updated: ${stats.updated}\nSkipped/Not Found: ${stats.skipped}`);
            }
            
            await fetchPersonnel();
        } catch (err) {
            console.error("CSV upload failed", err);
            alert(err.response?.data?.error || "Failed to upload and process CSV file.");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Reset file input
            }
        }
    };


    // Extract unique groups for the dropdown based on selected role
    const uniqueGroups = useMemo(() => {
        const isRoleRetailer = selectedRole && selectedRole.toLowerCase() === 'retailer';

        const groups = personnel
            .filter(p => {
                const isUserRetailer = p.role && p.role.toLowerCase() === 'retailer';
                // If "retailer" role is selected, only show retailer groups.
                // Otherwise, exclude retailer groups from the list.
                return isRoleRetailer ? isUserRetailer : !isUserRetailer;
            })
            .map(p => p.group_name)
            .filter(Boolean);

        return [...new Set(groups)].sort();
    }, [personnel, selectedRole]);

    // Reset selected group if it is no longer available in the filtered uniqueGroups list
    useEffect(() => {
        if (selectedGroup && !uniqueGroups.includes(selectedGroup)) {
            setSelectedGroup('');
        }
    }, [uniqueGroups, selectedGroup]);

    // Filter groups based on searchable input
    const filteredGroupsList = useMemo(() => {
        return uniqueGroups.filter(group => 
            group.toLowerCase().includes(groupSearchQuery.toLowerCase())
        );
    }, [uniqueGroups, groupSearchQuery]);

    // Extract unique roles for the dropdown
    const uniqueRoles = useMemo(() => {
        const roles = personnel.map(p => p.role).filter(Boolean);
        return [...new Set(roles)].sort();
    }, [personnel]);

    // Filter the personnel data based on search, group, role, password presence, and standard email validation
    const filteredPersonnel = useMemo(() => {
        // Standard email validation regex (requires something@something.domain)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return personnel.filter(p => {
            // 1. Hide if the email is missing or does not match standard email format
            if (!p.work_email || !emailRegex.test(p.work_email)) {
                return false;
            }

            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = 
                (p.first_name || '').toLowerCase().includes(searchLower) ||
                (p.last_name || '').toLowerCase().includes(searchLower) ||
                (p.work_email || '').toLowerCase().includes(searchLower) ||
                (p.company || '').toLowerCase().includes(searchLower) ||
                (p.group_name || '').toLowerCase().includes(searchLower);
            
            const matchesGroup = selectedGroup ? p.group_name === selectedGroup : true;
            const matchesRole = selectedRole ? p.role === selectedRole : true;
            
            // Filter for accounts with no password (null, undefined, or empty string)
            const matchesNoPassword = showNoPasswordOnly ? (!p.password || p.password.trim() === '') : true;

            // Filter to hide retailers if checked
            const matchesHideRetailers = hideRetailers ? (p.role || '').toLowerCase() !== 'retailer' : true;

            return matchesSearch && matchesGroup && matchesRole && matchesNoPassword && matchesHideRetailers;
        });
    }, [personnel, searchTerm, selectedGroup, selectedRole, showNoPasswordOnly, hideRetailers]);

    // Pagination Calculations
    const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
    const paginatedPersonnel = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredPersonnel.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredPersonnel, currentPage, itemsPerPage]);

    // Handlers for password editing
    const handleTogglePasswordEdit = () => {
        if (!isEditingPasswords) {
            const drafts = {};
            personnel.forEach(p => {
                drafts[p.id] = p.password || '';
            });
            setPasswordDrafts(drafts);
        } else {
            setPasswordDrafts({});
        }
        setIsEditingPasswords(!isEditingPasswords);
    };

    const handlePasswordChange = (id, value) => {
        setPasswordDrafts(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSavePassword = async (id) => {
        const newPassword = passwordDrafts[id];
        if (!newPassword) {
            alert("Password cannot be empty.");
            return;
        }
        
        try {
            await api.put(`/personnel/${id}`, { password: newPassword });
            alert("Password updated successfully!");
            await fetchPersonnel();
            setPasswordDrafts(prev => {
                const newDrafts = { ...prev };
                delete newDrafts[id];
                return newDrafts;
            });
        } catch (err) {
            console.error("Failed to update password", err);
            alert("Failed to update password.");
        }
    };

    if (isLoading && personnel.length === 0) {
        return <div className="card"><p>Loading personnel information...</p></div>;
    }

    return (
        <div className="card">
            {/* Header with Sync and Upload Buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div className="component-header" style={{ marginBottom: 0 }}>
                    <h3 style={{ borderBottom: 'none', marginBottom: '5px' }}>Personnel Records</h3>
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    {/* Hidden File Input */}
                    <input 
                        type="file" 
                        accept=".csv" 
                        ref={fileInputRef} 
                        onChange={handleCsvUpload} 
                        style={{ display: 'none' }} 
                    />
                    
                    <button 
                        className="btn" 
                        onClick={() => fileInputRef.current.click()} 
                        disabled={isUploading}
                        style={{ height: 'fit-content', backgroundColor: isUploading ? '#ccc' : 'var(--accent, #28a745)' }}
                    >
                        {isUploading ? 'Uploading...' : 'Bulk Upload Passwords (CSV)'}
                    </button>

                    <button 
                        className="btn" 
                        onClick={handleSync} 
                        disabled={isSyncing}
                        style={{ height: 'fit-content', backgroundColor: isSyncing ? '#ccc' : 'var(--primary)' }}
                    >
                        {isSyncing ? 'Syncing...' : 'Sync with External API'}
                    </button>
                </div>
            </div>

            {/* Filters, Search Bar, and Password Toggle */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input 
                    type="text" 
                    placeholder="Search name, email, business, or group..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ flex: '1', minWidth: '200px', margin: 0 }}
                />
                
                {/* Searchable Group Dropdown */}
                <div ref={groupDropdownRef} style={{ position: 'relative', minWidth: '200px' }}>
                    <div 
                        onClick={() => setIsGroupDropdownOpen(!isGroupDropdownOpen)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <span style={{ color: selectedGroup ? '#000' : '#757575', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {selectedGroup || "All Groups"}
                        </span>
                        <span>▼</span>
                    </div>

                    {isGroupDropdownOpen && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            marginTop: '4px',
                            maxHeight: '250px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <input 
                                type="text"
                                placeholder="Search group..."
                                value={groupSearchQuery}
                                onChange={(e) => setGroupSearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()} // Prevent closing dropdown
                                style={{
                                    margin: '8px',
                                    padding: '6px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px'
                                }}
                            />
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                <div 
                                    onClick={() => { setSelectedGroup(''); setIsGroupDropdownOpen(false); setGroupSearchQuery(''); }}
                                    style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: selectedGroup === '' ? '#f0f0f0' : 'transparent' }}
                                >
                                    All Groups
                                </div>
                                {filteredGroupsList.map(group => (
                                    <div 
                                        key={group}
                                        onClick={() => { setSelectedGroup(group); setIsGroupDropdownOpen(false); setGroupSearchQuery(''); }}
                                        style={{ 
                                            padding: '8px 12px', 
                                            cursor: 'pointer', 
                                            backgroundColor: selectedGroup === group ? '#e0e0e0' : 'transparent',
                                            borderTop: '1px solid #f0f0f0'
                                        }}
                                    >
                                        {group}
                                    </div>
                                ))}
                                {filteredGroupsList.length === 0 && (
                                    <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>No groups found</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <select 
                    value={selectedRole} 
                    onChange={(e) => setSelectedRole(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
                >
                    <option value="">All Roles</option>
                    {uniqueRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                    ))}
                </select>

                {/* Hide Retailers Filter Checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    <input 
                        type="checkbox" 
                        checked={hideRetailers}
                        onChange={(e) => setHideRetailers(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Hide Retailers
                </label>

                {/* No Password Filter Checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    <input 
                        type="checkbox" 
                        checked={showNoPasswordOnly}
                        onChange={(e) => setShowNoPasswordOnly(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    No Password Only
                </label>

                {/* Password Edit Toggle Switch */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', marginLeft: 'auto' }}>
                    <input 
                        type="checkbox" 
                        checked={isEditingPasswords}
                        onChange={handleTogglePasswordEdit}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Edit Passwords
                </label>
            </div>

            {error && <p className="message error">{error}</p>}

            <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Business Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Group</th>
                            <th>Mobile Number</th>
                            <th>Password</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedPersonnel.length > 0 ? (
                            paginatedPersonnel.map(p => (
                                <tr key={p.id}>
                                    <td>{p.first_name || <span className="italic-placeholder">N/A</span>}</td>
                                    <td>{p.company || <span className="italic-placeholder">N/A</span>}</td>
                                    <td>{p.work_email || <span className="italic-placeholder">N/A</span>}</td>
                                    <td>{p.role || <span className="italic-placeholder">N/A</span>}</td>
                                    <td>{p.group_name || <span className="italic-placeholder">N/A</span>}</td>
                                    <td>{p.work_mobile || <span className="italic-placeholder">N/A</span>}</td>
                                    <td style={{ minWidth: '200px' }}>
                                        {isEditingPasswords ? (
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <input 
                                                    type="text" 
                                                    placeholder="Password" 
                                                    value={passwordDrafts[p.id] !== undefined ? passwordDrafts[p.id] : (p.password || '')}
                                                    onChange={(e) => handlePasswordChange(p.id, e.target.value)}
                                                    style={{ margin: 0, padding: '4px', width: '120px' }}
                                                />
                                                <button 
                                                    className="btn" 
                                                    style={{ padding: '4px 8px', fontSize: '12px' }}
                                                    onClick={() => handleSavePassword(p.id)}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        ) : (
                                            <span>{p.password || <span className="italic-placeholder">N/A</span>}</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="no-data-message">
                                    No personnel records found matching your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination & Items Per Page Controls */}
            {filteredPersonnel.length > 0 && (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginTop: '20px', 
                    flexWrap: 'wrap', 
                    gap: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid #eee'
                }}>
                    {/* Items Per Page Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#555' }}>Show:</span>
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer' }}
                        >
                            <option value={15}>15 per page</option>
                            <option value={30}>30 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>
                        <span style={{ fontSize: '14px', color: '#777', marginLeft: '10px' }}>
                            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredPersonnel.length)} to {Math.min(currentPage * itemsPerPage, filteredPersonnel.length)} of {filteredPersonnel.length} records
                        </span>
                    </div>

                    {/* Page Turner Controls */}
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <button 
                            className="btn" 
                            style={{ padding: '6px 12px', backgroundColor: currentPage === 1 ? '#ccc' : 'var(--primary)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                        >
                            « First
                        </button>
                        <button 
                            className="btn" 
                            style={{ padding: '6px 12px', backgroundColor: currentPage === 1 ? '#ccc' : 'var(--primary)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            ‹ Prev
                        </button>
                        
                        <span style={{ padding: '0 15px', fontWeight: 'bold', fontSize: '14px' }}>
                            Page {currentPage} of {totalPages || 1}
                        </span>

                        <button 
                            className="btn" 
                            style={{ padding: '6px 12px', backgroundColor: currentPage === totalPages || totalPages === 0 ? '#ccc' : 'var(--primary)', cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || totalPages === 0}
                        >
                            Next ›
                        </button>
                        <button 
                            className="btn" 
                            style={{ padding: '6px 12px', backgroundColor: currentPage === totalPages || totalPages === 0 ? '#ccc' : 'var(--primary)', cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages || totalPages === 0}
                        >
                            Last »
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Personnel;
